import React, { useState, useEffect } from 'react';
import { Save, TestTube, CheckCircle, XCircle, AlertTriangle, Cpu, Sparkles, MessageSquare, Image, Mic } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';

const AdminAIIntegration = () => {
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [settings, setSettings] = useState({
    // OpenAI
    openaiEnabled: false,
    openaiApiKey: '',
    openaiModel: 'gpt-4',
    openaiMaxTokens: 2000,
    
    // Google Gemini
    geminiEnabled: false,
    geminiApiKey: '',
    geminiModel: 'gemini-pro',
    
    // Anthropic Claude
    claudeEnabled: false,
    claudeApiKey: '',
    claudeModel: 'claude-3-sonnet',
    
    // Default Provider
    defaultProvider: 'openai',
    
    // Features
    enableEmailGeneration: true,
    enableLeadAnalysis: true,
    enableChatbot: false,
    enableTranscription: false,
    
    // Limits
    maxRequestsPerDay: 1000,
    maxTokensPerRequest: 4000,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getAISettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('Using default AI settings');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveAISettings(settings);
      toast.success('Configuración de IA guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: 'testing' }));
    try {
      await api.testAIConnection(provider);
      setTesting(prev => ({ ...prev, [provider]: 'success' }));
      toast.success(`Conexión con ${provider} exitosa`);
    } catch (error) {
      setTesting(prev => ({ ...prev, [provider]: 'error' }));
      toast.error(`Error al conectar con ${provider}`);
    }
  };

  const renderConnectionStatus = (provider) => {
    const status = testing[provider];
    if (status === 'testing') {
      return <div className="animate-spin w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full" />;
    }
    if (status === 'success') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    if (status === 'error') {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
    return null;
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Integración con Servicios de IA</h1>
        <p className="text-slate-500 mt-2">Configura los proveedores de inteligencia artificial para tu aplicación</p>
      </div>

      <div className="space-y-8">
        {/* Default Provider */}
        <section className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Proveedor Principal de IA</h2>
              <p className="text-indigo-100 text-sm mt-1">Este será el servicio usado por defecto para todas las funciones de IA</p>
            </div>
            <select
              value={settings.defaultProvider}
              onChange={(e) => handleChange('defaultProvider', e.target.value)}
              className="px-4 py-2 bg-white/20 backdrop-blur border border-white/30 rounded-lg text-white focus:outline-none"
            >
              <option value="openai" className="text-slate-900">OpenAI (GPT-4)</option>
              <option value="gemini" className="text-slate-900">Google Gemini</option>
              <option value="claude" className="text-slate-900">Anthropic Claude</option>
            </select>
          </div>
        </section>

        {/* OpenAI */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">OpenAI</h2>
                <p className="text-sm text-slate-500">GPT-4, GPT-3.5, DALL-E, Whisper</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.openaiEnabled}
                onChange={(e) => handleChange('openaiEnabled', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <span className="text-sm font-medium text-slate-700">Activado</span>
            </label>
          </div>

          {settings.openaiEnabled && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  API Key
                </label>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={settings.openaiApiKey}
                    onChange={(e) => handleChange('openaiApiKey', e.target.value)}
                    placeholder="sk-..."
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => testConnection('openai')}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    {renderConnectionStatus('openai') || <TestTube className="w-4 h-4" />}
                    <span>Probar</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Obtén tu API Key en <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">platform.openai.com</a>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Modelo</label>
                  <select
                    value={settings.openaiModel}
                    onChange={(e) => handleChange('openaiModel', e.target.value)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  >
                    <option value="gpt-4-turbo">GPT-4 Turbo (Recomendado)</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Económico)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Max Tokens</label>
                  <input
                    type="number"
                    value={settings.openaiMaxTokens}
                    onChange={(e) => handleChange('openaiMaxTokens', parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Google Gemini */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cpu className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Google Gemini</h2>
                <p className="text-sm text-slate-500">Gemini Pro, Gemini Ultra</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.geminiEnabled}
                onChange={(e) => handleChange('geminiEnabled', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <span className="text-sm font-medium text-slate-700">Activado</span>
            </label>
          </div>

          {settings.geminiEnabled && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  API Key
                </label>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={settings.geminiApiKey}
                    onChange={(e) => handleChange('geminiApiKey', e.target.value)}
                    placeholder="AIza..."
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => testConnection('gemini')}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    {renderConnectionStatus('gemini') || <TestTube className="w-4 h-4" />}
                    <span>Probar</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Obtén tu API Key en <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Google AI Studio</a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Modelo</label>
                <select
                  value={settings.geminiModel}
                  onChange={(e) => handleChange('geminiModel', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="gemini-pro">Gemini Pro</option>
                  <option value="gemini-pro-vision">Gemini Pro Vision (con imágenes)</option>
                </select>
              </div>
            </div>
          )}
        </section>

        {/* Anthropic Claude */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Anthropic Claude</h2>
                <p className="text-sm text-slate-500">Claude 3 Opus, Sonnet, Haiku</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.claudeEnabled}
                onChange={(e) => handleChange('claudeEnabled', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <span className="text-sm font-medium text-slate-700">Activado</span>
            </label>
          </div>

          {settings.claudeEnabled && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  API Key
                </label>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={settings.claudeApiKey}
                    onChange={(e) => handleChange('claudeApiKey', e.target.value)}
                    placeholder="sk-ant-..."
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => testConnection('claude')}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    {renderConnectionStatus('claude') || <TestTube className="w-4 h-4" />}
                    <span>Probar</span>
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Obtén tu API Key en <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">console.anthropic.com</a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Modelo</label>
                <select
                  value={settings.claudeModel}
                  onChange={(e) => handleChange('claudeModel', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="claude-3-opus">Claude 3 Opus (Más potente)</option>
                  <option value="claude-3-sonnet">Claude 3 Sonnet (Balanceado)</option>
                  <option value="claude-3-haiku">Claude 3 Haiku (Más rápido)</option>
                </select>
              </div>
            </div>
          )}
        </section>

        {/* AI Features */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Funciones de IA</h2>
              <p className="text-sm text-slate-500">Activa las características de IA en tu aplicación</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enableEmailGeneration}
                onChange={(e) => handleChange('enableEmailGeneration', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <div>
                <p className="font-medium text-slate-900">Generación de Emails</p>
                <p className="text-sm text-slate-500">Genera emails personalizados para leads</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enableLeadAnalysis}
                onChange={(e) => handleChange('enableLeadAnalysis', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <div>
                <p className="font-medium text-slate-900">Análisis de Leads</p>
                <p className="text-sm text-slate-500">Analiza y sugiere acciones para leads</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enableChatbot}
                onChange={(e) => handleChange('enableChatbot', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <div>
                <p className="font-medium text-slate-900">Chatbot Asistente</p>
                <p className="text-sm text-slate-500">Asistente de IA para consultas</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enableTranscription}
                onChange={(e) => handleChange('enableTranscription', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
              <div>
                <p className="font-medium text-slate-900">Transcripción de Audio</p>
                <p className="text-sm text-slate-500">Transcribe reuniones y llamadas</p>
              </div>
            </label>
          </div>
        </section>

        {/* Usage Limits */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Límites de Uso</h2>
              <p className="text-sm text-slate-500">Controla el consumo de la API para evitar costos excesivos</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Máx. solicitudes por día
              </label>
              <input
                type="number"
                value={settings.maxRequestsPerDay}
                onChange={(e) => handleChange('maxRequestsPerDay', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Máx. tokens por solicitud
              </label>
              <input
                type="number"
                value={settings.maxTokensPerRequest}
                onChange={(e) => handleChange('maxTokensPerRequest', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg"
              />
            </div>
          </div>
        </section>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAIIntegration;
